import { useLayoutEffect } from 'react';
import { usePrevious } from 'react-use';

import { PatientPresence } from '@dreryk/egabinet-appointment-frontend-api';

import { visitPaths } from '~libs.desktop.shared.paths';

import {
   FILTER_SEPARATOR,
   getPatientPresenceFilterSearchParamValue,
   getPatientPresenceFromSearchParam,
   mapPatientPresenceFilterParamToPatientPresence,
} from './PatientPresenceField.utils';
import { PatientPresenceFilterParam } from './PatientPresenceField.types';

const STORAGE_PREFIX = 'egabinet-desktop:';
const PATIENT_PRESENCE_FILTER_STORAGE_KEY = `${STORAGE_PREFIX}patientPresence`;

type SetFilter = (filter: PatientPresenceFilterParam) => void;
type DeleteFilter = VoidFunction;
type GetStoredPatientPresences = () => PatientPresence[];

type PatientPresenceFilterStorageReturnFn = {
   setFilter: SetFilter;
   deleteFilter: DeleteFilter;
   getStoredPatientPresences: GetStoredPatientPresences;
};

export const patientPresenceFilterStorage: PatientPresenceFilterStorageReturnFn = (() => {
   const deleteFilter: DeleteFilter = () => window.sessionStorage.removeItem(PATIENT_PRESENCE_FILTER_STORAGE_KEY);
   const setFilter: SetFilter = (filter) => {
      if (filter === '') {
         return deleteFilter();
      }

      return window.sessionStorage.setItem(PATIENT_PRESENCE_FILTER_STORAGE_KEY, filter);
   };

   const getStoredPatientPresences: GetStoredPatientPresences = () =>
      mapPatientPresenceFilterParamToPatientPresence(
         window.sessionStorage.getItem(PATIENT_PRESENCE_FILTER_STORAGE_KEY)
      );

   return {
      setFilter: setFilter,
      getStoredPatientPresences: getStoredPatientPresences,
      deleteFilter: deleteFilter,
   };
})();

export const useSyncPatientPresenceFilterParamWithStorage = (
   searchParams: URLSearchParams,
   onSync: (searchParams: URLSearchParams) => void
): void => {
   const patientPresenceFilterParam = searchParams.get(visitPaths.PATIENT_PRESENCE_SEARCH_PARAM);
   const previousPatientPresenceFilterParam = usePrevious(patientPresenceFilterParam);
   const memoizedPatientPresenceFilter = patientPresenceFilterStorage.getStoredPatientPresences();

   useLayoutEffect(() => {
      if (patientPresenceFilterParam && patientPresenceFilterParam !== previousPatientPresenceFilterParam) {
         const patientPresencesFromSearchParam = patientPresenceFilterParam.split(FILTER_SEPARATOR).map(Number);
         const patientPresencesValid = patientPresencesFromSearchParam.every((id) => !isNaN(id));

         if (!patientPresencesValid) {
            searchParams.delete(visitPaths.PATIENT_PRESENCE_SEARCH_PARAM);
            onSync(searchParams);
         }

         if (patientPresencesValid) {
            patientPresenceFilterStorage.setFilter(patientPresenceFilterParam);
         }
      }

      if (memoizedPatientPresenceFilter.length) {
         const patientPresencesFromSearchParam = patientPresenceFilterParam?.split(FILTER_SEPARATOR).map(Number);
         const memoizedPresencesAsSet = new Set(memoizedPatientPresenceFilter);

         if (
            !patientPresencesFromSearchParam ||
            patientPresencesFromSearchParam.some((searchParamValue) => {
               const presenceFromSearchParam = getPatientPresenceFromSearchParam(searchParamValue);
               return !presenceFromSearchParam || !memoizedPresencesAsSet.has(presenceFromSearchParam);
            })
         ) {
            const newPatientPresenceFilterParam = getPatientPresenceFilterSearchParamValue(
               memoizedPatientPresenceFilter
            );
            searchParams.set(visitPaths.PATIENT_PRESENCE_SEARCH_PARAM, newPatientPresenceFilterParam);
            onSync(searchParams);
         }
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
   }, [patientPresenceFilterParam, memoizedPatientPresenceFilter]);
};
